import React, { useEffect, useRef, useCallback } from "react";
import "./common.scss";

const Modal = React.memo(
  ({
    isOpen,
    onClose,
    title,
    btnTitle = "Close",
    children,
    type = "success",
    modalNode
  }) => {
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);

    // Trap focus inside the modal
    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Escape") {
          onClose();
          return;
        }
        if (e.key !== "Tab") return;

        const modal = modalRef.current;
        if (!modal) return;
        const focusable = modal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      },
      [onClose],
    );

    // Focus management: save previous focus, auto-focus modal, restore on close
    useEffect(() => {
      if (isOpen) {
        previousFocusRef.current = document.activeElement;
        // Auto-focus first focusable element inside modal after render
        requestAnimationFrame(() => {
          const modal = modalRef.current;
          if (!modal) return;
          const first = modal.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );
          if (first) first.focus();
        });
      } else if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div
          ref={modalRef}
          className={`modal-container ${type}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
        >
          <div className="modal-header-top">
            {title &&
              <h3 className="modal-title" id="modal-title">
                {title}
              </h3>
            }
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                  fill="#3C3D3D"
                />
              </svg>
            </button>
          </div>
          <div className="modal-contents">{children}</div>
          {modalNode !== "session" &&
            <div className="modal-footer">
              <button className=" btn primary-button regular" onClick={onClose}>
                {btnTitle}
              </button>
            </div>
          }
        </div>
      </div>
    );
  },
);

Modal.displayName = "Modal";

export default Modal;
